import { ExpandMore } from '@mui/icons-material';
import { AccordionDetails, Box, Typography } from '@mui/material';
import * as R from 'ramda';
import { useState } from 'react';

import {
  Accordion,
  AccordionSummary,
  CheckboxButton,
  FlagIcon,
} from '@/components';
import {
  DesignationsForm,
  onboardingActions,
  selectCountryOptions,
  selectDesignationTypeOptions,
  selectOnboardingStepInputs,
  STEP_IDS,
  useAppDispatch,
  useAppSelector,
  useFetchDesignationOptionsQuery,
} from '@/store';

import { Link } from '../../Link';
import { styles } from '../styles';
import { Footer } from './Footer';
import { Header } from './Header';
import { ModalSubtitle } from './ModalSubtitle';
import { ModalTitle } from './ModalTitle';

export const DesignationTypesForm = () => {
  const dispatch = useAppDispatch();

  const stepInputs = useAppSelector(selectOnboardingStepInputs);

  const { value: designations }: { value: DesignationsForm } =
    stepInputs[STEP_IDS.designationTypes];

  const { designationTypeOptions, countryOptions } =
    useFetchDesignationOptionsQuery(undefined, {
      selectFromResult: result => ({
        ...result,
        designationTypeOptions: selectDesignationTypeOptions(result.data),
        countryOptions: selectCountryOptions(result.data),
      }),
    });

  const [selectedCountry, setSelectedCountry] = useState<string>();

  const handleChange = (countryCode: string, designationCode: string) => {
    const countryDesignations = designations[countryCode] || {};
    const isChecked = !!countryDesignations[designationCode];

    dispatch(
      onboardingActions.updateStepInputs({
        step: STEP_IDS.designationTypes,
        input: isChecked
          ? {
              ...designations,
              [countryCode]: R.omit(
                [designationCode],
                designations[countryCode],
              ),
            }
          : {
              ...designations,
              [countryCode]: {
                ...designations[countryCode],
                [designationCode]: true,
              },
            },
      }),
    );
  };

  const handleAccordionChange = (countryCode: string, isExpanded: boolean) => {
    setSelectedCountry(isExpanded ? countryCode : undefined);
  };

  return (
    <>
      <Box sx={styles.formContainer}>
        <Header Title={<Title />} Subtitle={<Subtitle />} />
        <Box sx={styles.formPrimarySection}>
          {Object.keys(designationTypeOptions).map(countryCode => (
            <Accordion
              disableGutters
              key={countryCode}
              expanded={selectedCountry === countryCode}
              onChange={(_, isExpanded) =>
                handleAccordionChange(countryCode, isExpanded)
              }>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  <FlagIcon region={countryCode} />
                </Box>
                <Typography variant="body2SemiBold" component="span">
                  {countryOptions[countryCode].name}{' '}
                  {Object.keys(designations[countryCode] || {}).length ? (
                    <span>
                      ({Object.keys(designations[countryCode]).length})
                    </span>
                  ) : null}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {selectedCountry === countryCode && (
                  <Box sx={{ width: '100%' }}>
                    {Object.entries(
                      designationTypeOptions?.[selectedCountry] || {},
                    ).map(([designationCode, designationLabel]) => (
                      <Box
                        key={selectedCountry + designationCode}
                        sx={{
                          mb: 1,
                        }}>
                        <CheckboxButton
                          id={designationCode}
                          label={designationLabel || ''}
                          checked={
                            !!designations?.[selectedCountry]?.[designationCode]
                          }
                          onChange={() =>
                            handleChange(countryCode, designationCode)
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        <Footer />
      </Box>
    </>
  );
};

const Title = () => (
  <ModalTitle>
    Select your designation(s) from the list below. Check all that apply.
  </ModalTitle>
);

const Subtitle = () => (
  <ModalSubtitle>
    Don&apos;t see your country or region&apos;s designation?{' '}
    <Link inline onClick={() => window.Intercom?.('show')}>
      Let us know
    </Link>
  </ModalSubtitle>
);
